import React, { useState, useEffect } from "react";
import { VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY } from "@/utils/const";
import { motion } from "framer-motion";
import {
	Calendar,
	Clock,
	MapPin,
	Video,
	MessageSquare,
	ArrowLeft,
	CheckCircle,
	XCircle,
	AlertCircle,
	ExternalLink,
	CalendarDays,
	Clock3,
	User,
	Building2,
	Baby,
	Check,
	X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface Appointment {
	id: string;
	parent_user_id: string;
	appointment_date: string;
	status: "pending" | "accepted" | "declined" | "cancelled" | "completed";
	duration_minutes: number;
	meeting_link?: string;
	meeting_id?: string;
	notes?: string;
	parent: {
		id: string;
		full_name: string;
		avatar_url: string;
		email: string;
	};
	parent_profile: {
		due_date: string;
		birth_location?: string;
	};
}

const DoulaBookings: React.FC = () => {
	const { user, profile } = useAuth();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [appointments, setAppointments] = useState<Appointment[]>([]);
	const [selectedAppointment, setSelectedAppointment] =
		useState<Appointment | null>(null);
	const [filter, setFilter] = useState<"all" | "upcoming" | "past">("all");

	useEffect(() => {
		if (user) {
			loadAppointments();
		}
	}, [user]);

	const loadAppointments = async () => {
		if (!user) return;

		try {
			setLoading(true);

			// Get the public user ID (not auth user ID)
			const { data: publicUser, error: userError } = await supabase
				.from("users")
				.select("id")
				.eq("auth_user_id", user.id)
				.single();

			if (userError || !publicUser) {
				console.error("Error fetching public user:", userError);
				return;
			}

			const doulaUserId = publicUser.id;

			// Use edge function to get appointments
			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/get-doula-appointments`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						doulaUserId: doulaUserId,
						includePast: true,
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				console.error("Error loading appointments:", result.error);
				return;
			}

			setAppointments(result.data || []);
		} catch (error) {
			console.error("Error loading appointments:", error);
		} finally {
			setLoading(false);
		}
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	const getTimeUntilAppointment = (dateString: string) => {
		const appointmentDate = new Date(dateString);
		const now = new Date();
		const diff = appointmentDate.getTime() - now.getTime();

		if (diff < 0) return null; // Past appointment

		const days = Math.floor(diff / (1000 * 60 * 60 * 24));
		const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
		const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

		if (days > 0) {
			return `${days} day${days > 1 ? "s" : ""} ${hours} hour${hours > 1 ? "s" : ""}`;
		} else if (hours > 0) {
			return `${hours} hour${hours > 1 ? "s" : ""} ${minutes} minute${minutes > 1 ? "s" : ""}`;
		} else {
			return `${minutes} minute${minutes > 1 ? "s" : ""}`;
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800 border-green-200";
			case "accepted":
				return "bg-blue-100 text-blue-800 border-blue-200";
			case "pending":
				return "bg-yellow-100 text-yellow-800 border-yellow-200";
			case "declined":
				return "bg-red-100 text-red-800 border-red-200";
			case "cancelled":
				return "bg-gray-100 text-gray-800 border-gray-200";
			default:
				return "bg-gray-100 text-gray-800 border-gray-200";
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "completed":
				return <CheckCircle className="h-4 w-4" />;
			case "accepted":
				return <CheckCircle className="h-4 w-4" />;
			case "pending":
				return <Clock className="h-4 w-4" />;
			case "declined":
				return <XCircle className="h-4 w-4" />;
			case "cancelled":
				return <XCircle className="h-4 w-4" />;
			default:
				return <AlertCircle className="h-4 w-4" />;
		}
	};

	const filteredAppointments = appointments.filter((appointment) => {
		const appointmentDate = new Date(appointment.appointment_date);
		const now = new Date();

		if (filter === "upcoming") {
			return (
				appointmentDate > now &&
				appointment.status !== "cancelled" &&
				appointment.status !== "declined"
			);
		} else if (filter === "past") {
			return (
				appointmentDate < now ||
				appointment.status === "cancelled" ||
				appointment.status === "declined"
			);
		}
		return true;
	});

	const upcomingAppointments = filteredAppointments.filter((appointment) => {
		const appointmentDate = new Date(appointment.appointment_date);
		const now = new Date();
		return (
			appointmentDate > now &&
			appointment.status !== "cancelled" &&
			appointment.status !== "declined"
		);
	});

	const pastAppointments = filteredAppointments.filter((appointment) => {
		const appointmentDate = new Date(appointment.appointment_date);
		const now = new Date();
		return (
			appointmentDate < now ||
			appointment.status === "cancelled" ||
			appointment.status === "declined"
		);
	});

	const handleJoinMeeting = (appointment: Appointment) => {
		if (appointment.meeting_link) {
			window.open(appointment.meeting_link, "_blank");
		}
	};

	const handleMessageParent = (appointment: Appointment) => {
		// Navigate to messages with the specific parent
		navigate("/doula-messages", {
			state: {
				parentUserId: appointment.parent.id,
				appointmentId: appointment.id,
			},
		});
	};

	const canJoinMeeting = (appointmentDate: string) => {
		const appointmentTime = new Date(appointmentDate);
		const now = new Date();
		const tenMinutesBefore = new Date(
			appointmentTime.getTime() - 10 * 60 * 1000,
		);
		return now >= tenMinutesBefore;
	};

	const handleAcceptAppointment = async (appointmentId: string) => {
		try {
			setLoading(true);

			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-appointment-status`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						appointmentId: appointmentId,
						status: "accepted",
					}),
				},
			);

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(data.error || "Failed to accept appointment");
			}

			// Refresh appointments list to show updated status
			await loadAppointments();
		} catch (error: unknown) {
			console.error("Error accepting appointment:", error);
			// You could add a toast notification here
		} finally {
			setLoading(false);
		}
	};

	const handleDeclineAppointment = async (appointmentId: string) => {
		try {
			setLoading(true);

			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-appointment-status`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						appointmentId: appointmentId,
						status: "declined",
					}),
				},
			);

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(data.error || "Failed to decline appointment");
			}

			// Refresh appointments list to show updated status
			await loadAppointments();
		} catch (error: unknown) {
			console.error("Error declining appointment:", error);
			// You could add a toast notification here
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">
						Loading your appointments...
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="mb-8"
				>
					<div className="flex items-center space-x-4">
						<Button
							variant="ghost"
							onClick={() => navigate("/doula-dashboard")}
							className="p-2"
						>
							<ArrowLeft className="h-5 w-5" />
						</Button>
						<div>
							<h1 className="text-3xl font-light text-black font-abc-prophet">
								My Appointments
							</h1>
							<p className="text-black font-karla">
								Manage your client appointments
							</p>
						</div>
					</div>
				</motion.div>

				{/* Filter Tabs */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.1 }}
					className="mb-6"
				>
					<div className="flex space-x-2 bg-white rounded-lg p-1 shadow-sm">
						{[
							{ key: "all", label: "All", count: appointments.length },
							{
								key: "upcoming",
								label: "Upcoming",
								count: upcomingAppointments.length,
							},
							{ key: "past", label: "Past", count: pastAppointments.length },
						].map((tab) => (
							<button
								type="button"
								key={tab.key}
								onClick={() =>
									setFilter(tab.key as "all" | "upcoming" | "past")
								}
								className={`flex-1 py-2 px-4 rounded-md text-sm text-black font-medium transition-colors ${filter === tab.key
									? "bg-newTheme-lime shadow-btn-shadow"
									: "hover:bg-gray-100"
									}`}
							>
								{tab.label} <span className="tabCount">({tab.count})</span>
							</button>
						))}
					</div>
				</motion.div>

				{/* Appointments Grid */}
				<div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
					{filteredAppointments.length === 0 ? (
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="col-span-full"
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardContent className="text-center py-12">
									<Calendar className="h-16 w-16 text-newTheme-orange mx-auto mb-4" />
									<h3 className="text-lg font-medium text-black font-karla mb-2">
										No appointments found
									</h3>
									<p className="text-black font-karla mb-6">
										{filter === "upcoming"
											? "You don't have any upcoming appointments. Parents will book appointments with you here!"
											: filter === "past"
												? "You don't have any past appointments yet."
												: "You don't have any appointments yet. When parents book with you, they'll appear here!"}
									</p>
									<Button
										onClick={() => navigate("/doula-dashboard")}
										className="bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow hover:text-white"
									>
										<Calendar className="h-4 w-4 mr-2" />
										Back to Dashboard
									</Button>
								</CardContent>
							</Card>
						</motion.div>
					) : (
						filteredAppointments.map((appointment, index) => (
							<motion.div
								key={appointment.id}
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.1 + index * 0.1 }}
							>
								<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300">
									<CardHeader className="pb-4">
										<div className="flex items-start justify-between">
											<div className="flex items-center space-x-3">
												<div className="relative">
													<img
														src={
															appointment.parent.avatar_url ||
															"/placeholder.svg"
														}
														alt={appointment.parent.full_name}
														className="w-12 h-12 rounded-lg object-cover border-2 border-primary-200"
													/>
													<div className="absolute -bottom-1 -right-1 w-4 h-4 bg-primary-500 rounded-full border-2 border-white"></div>
												</div>
												<div>
													<h3 className="font-semibold text-gray-900 font-karla">
														{appointment.parent.full_name}
													</h3>
													<p className="text-sm text-gray-600 font-karla">
														Client
													</p>
												</div>
											</div>
											<Badge
												variant="outline"
												className={`text-xs ${getStatusColor(appointment.status)}`}
											>
												<span className="flex items-center space-x-1">
													{getStatusIcon(appointment.status)}
													<span className="capitalize">
														{appointment.status}
													</span>
												</span>
											</Badge>
										</div>
									</CardHeader>

									<CardContent className="space-y-4">
										{/* Appointment Details */}
										<div className="space-y-3">
											<div className="flex items-center space-x-3">
												<CalendarDays className="h-4 w-4 text-primary-600" />
												<div>
													<p className="text-sm font-medium text-gray-900 font-karla">
														{formatDate(appointment.appointment_date)}
													</p>
													<p className="text-xs text-gray-600 font-karla">
														{formatTime(appointment.appointment_date)} •{" "}
														{appointment.duration_minutes} minutes
													</p>
												</div>
											</div>

											{appointment.parent_profile.birth_location && (
												<div className="flex items-center space-x-3">
													<MapPin className="h-4 w-4 text-primary-600" />
													<p className="text-sm text-gray-600 font-karla">
														{appointment.parent_profile.birth_location}
													</p>
												</div>
											)}

											{appointment.parent_profile.due_date && (
												<div className="flex items-center space-x-3">
													<Baby className="h-4 w-4 text-primary-600" />
													<p className="text-sm text-gray-600 font-karla">
														Due:{" "}
														{new Date(
															appointment.parent_profile.due_date,
														).toLocaleDateString()}
													</p>
												</div>
											)}

											{appointment.notes && (
												<div className="bg-gray-50 p-3 rounded-lg">
													<p className="text-sm text-gray-700 font-karla">
														<strong>Notes:</strong> {appointment.notes}
													</p>
												</div>
											)}
										</div>

										{/* Countdown for upcoming appointments */}
										{appointment.status === "accepted" &&
											getTimeUntilAppointment(appointment.appointment_date) && (
												<div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
													<div className="flex items-center space-x-2">
														<Clock3 className="h-4 w-4 text-blue-600" />
														<p className="text-sm font-medium text-blue-800 font-karla">
															Meeting starts in{" "}
															{getTimeUntilAppointment(
																appointment.appointment_date,
															)}
														</p>
													</div>
												</div>
											)}

										{/* Action Buttons */}
										<div className="flex flex-col space-y-2 pt-2">
											{/* Accept/Decline buttons for pending appointments */}
											{appointment.status === "pending" && (
												<div className="grid grid-cols-2 gap-2 mb-2">
													<Button
														onClick={() =>
															handleAcceptAppointment(appointment.id)
														}
														disabled={loading}
														className="bg-green-500 hover:bg-green-600 text-white text-sm"
													>
														<Check className="h-4 w-4 mr-1" />
														Accept
													</Button>
													<Button
														onClick={() =>
															handleDeclineAppointment(appointment.id)
														}
														disabled={loading}
														variant="outline"
														className="border-red-300 text-red-600 hover:bg-red-50 text-sm"
													>
														<X className="h-4 w-4 mr-1" />
														Decline
													</Button>
												</div>
											)}

											<Button
												onClick={() => handleMessageParent(appointment)}
												variant="outline"
												className="w-full text-sm"
											>
												<MessageSquare className="h-4 w-4 mr-2" />
												Message
											</Button>

											{appointment.status === "accepted" &&
												appointment.meeting_link && (
													<Button
														onClick={() => handleJoinMeeting(appointment)}
														disabled={
															!canJoinMeeting(appointment.appointment_date)
														}
														className={`w-full text-sm ${canJoinMeeting(appointment.appointment_date)
															? "bg-primary-500 hover:bg-primary-600 text-white"
															: "bg-primary-200 text-primary-600 cursor-not-allowed"
															}`}
													>
														<Video className="h-4 w-4 mr-2" />
														{canJoinMeeting(appointment.appointment_date)
															? "Join Meeting"
															: "Join Meeting (10 min before)"}
													</Button>
												)}
										</div>

										{/* Meeting Link Display */}
										{appointment.meeting_link && (
											<div className="pt-3 border-t border-gray-100">
												<div className="flex items-center justify-between text-xs text-gray-500 font-karla">
													<div className="flex items-center space-x-2">
														<Video className="h-3 w-3" />
														<span className="truncate">
															{appointment.meeting_link}
														</span>
													</div>
													<Button
														variant="ghost"
														size="sm"
														className="text-xs p-1 h-auto"
														onClick={() => setSelectedAppointment(appointment)}
													>
														<User className="h-3 w-3 mr-1" />
														View Profile
													</Button>
												</div>
											</div>
										)}
									</CardContent>
								</Card>
							</motion.div>
						))
					)}
				</div>
			</div>

			{/* Parent Profile Modal */}
			{selectedAppointment && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.9 }}
						className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
					>
						<div className="p-6">
							<div className="flex items-center justify-between mb-4">
								<h3 className="text-lg font-semibold font-karla">
									Client Profile
								</h3>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setSelectedAppointment(null)}
								>
									<XCircle className="h-4 w-4" />
								</Button>
							</div>

							<div className="space-y-4">
								<div className="flex items-center space-x-3">
									<img
										src={
											selectedAppointment.parent.avatar_url ||
											"/placeholder.svg"
										}
										alt={selectedAppointment.parent.full_name}
										className="w-16 h-16 rounded-full object-cover"
									/>
									<div>
										<h4 className="font-semibold text-gray-900 font-karla">
											{selectedAppointment.parent.full_name}
										</h4>
										<p className="text-sm text-gray-600 font-karla">Client</p>
									</div>
								</div>

								<div className="space-y-2">
									{selectedAppointment.parent_profile.birth_location && (
										<div className="flex items-center space-x-2">
											<Building2 className="h-4 w-4 text-primary-600" />
											<span className="text-sm text-gray-600 font-karla">
												{selectedAppointment.parent_profile.birth_location}
											</span>
										</div>
									)}

									{selectedAppointment.parent_profile.due_date && (
										<div className="flex items-center space-x-2">
											<Baby className="h-4 w-4 text-primary-600" />
											<span className="text-sm text-gray-600 font-karla">
												Due:{" "}
												{new Date(
													selectedAppointment.parent_profile.due_date,
												).toLocaleDateString()}
											</span>
										</div>
									)}

									{selectedAppointment.meeting_link && (
										<div className="flex items-center space-x-2">
											<Video className="h-4 w-4 text-primary-600" />
											<span className="text-sm text-gray-600 font-karla">
												Meeting link available
											</span>
										</div>
									)}
								</div>

								<div className="flex space-x-2 pt-4">
									<Button
										onClick={() => handleMessageParent(selectedAppointment)}
										className="flex-1 bg-primary-500 hover:bg-primary-600"
									>
										<MessageSquare className="h-4 w-4 mr-2" />
										Message
									</Button>

									<Button
										onClick={() => setSelectedAppointment(null)}
										variant="outline"
										className="flex-1"
									>
										<ExternalLink className="h-4 w-4 mr-2" />
										Close
									</Button>
								</div>
							</div>
						</div>
					</motion.div>
				</div>
			)}
		</div>
	);
};

export default DoulaBookings;
